(() => {
  const imageUrl = (src, width) => {
    if (!src) return '';
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };

  const initialize = (builder) => {
    if (builder.dataset.variantGalleryReady === 'true') return;

    const mainImage = builder.querySelector('[data-gallery-image]');
    const firstThumb = builder.querySelector('[data-gallery-thumb]');
    const thumbImage = firstThumb?.querySelector('img');
    if (!mainImage || !firstThumb || !thumbImage) return;

    builder.dataset.variantGalleryReady = 'true';
    const original = {
      src: mainImage.src,
      srcset: mainImage.srcset,
      alt: mainImage.alt,
      width: mainImage.getAttribute('width'),
      height: mainImage.getAttribute('height'),
      thumbSrc: thumbImage.src,
      thumbDataSrc: firstThumb.dataset.src || '',
      thumbDataSrcset: firstThumb.dataset.srcset || '',
      thumbAlt: firstThumb.dataset.alt || ''
    };
    let activeVariantId = null;

    const update = () => {
      const row = builder.partyRows?.[0];
      const variant = row && typeof builder.selectedVariant === 'function'
        ? builder.selectedVariant(row)
        : null;
      const variantId = String(variant?.id || '');
      if (variantId === activeVariantId) return;
      activeVariantId = variantId;

      const image = variant?.featured_image;
      if (image?.src) {
        mainImage.src = imageUrl(image.src, 1440);
        mainImage.srcset = `${imageUrl(image.src, 640)} 640w, ${imageUrl(image.src, 960)} 960w, ${imageUrl(image.src, 1440)} 1440w`;
        mainImage.alt = image.alt || variant.name || 'Selected product variant';
        if (image.width) mainImage.setAttribute('width', image.width);
        if (image.height) mainImage.setAttribute('height', image.height);
        firstThumb.dataset.src = mainImage.src;
        firstThumb.dataset.srcset = mainImage.srcset;
        firstThumb.dataset.alt = mainImage.alt;
        thumbImage.src = imageUrl(image.src, 180);
      } else {
        mainImage.src = original.src;
        mainImage.srcset = original.srcset;
        mainImage.alt = original.alt;
        original.width ? mainImage.setAttribute('width', original.width) : mainImage.removeAttribute('width');
        original.height ? mainImage.setAttribute('height', original.height) : mainImage.removeAttribute('height');
        firstThumb.dataset.src = original.thumbDataSrc;
        firstThumb.dataset.srcset = original.thumbDataSrcset;
        firstThumb.dataset.alt = original.thumbAlt;
        thumbImage.src = original.thumbSrc;
      }

      builder.querySelectorAll('[data-gallery-thumb]').forEach((thumb, index) => {
        thumb.classList.toggle('is-active', index === 0);
        thumb.setAttribute('aria-pressed', String(index === 0));
      });
      const count = builder.querySelector('[data-gallery-count]');
      const gallery = builder.querySelector('[data-gallery]');
      if (count) count.textContent = `1 / ${gallery?.dataset.galleryTotal || builder.querySelectorAll('[data-gallery-thumb]').length}`;
    };

    const rows = builder.querySelector('[data-builder-rows]');
    if (rows) new MutationObserver(() => requestAnimationFrame(update)).observe(rows, { childList: true });
    builder.addEventListener('change', () => requestAnimationFrame(update));
    update();
  };

  customElements.whenDefined('love-spell-builder').then(() => {
    document.querySelectorAll('love-spell-builder').forEach(initialize);
  });
  customElements.whenDefined('apex-pj-builder').then(() => {
    document.querySelectorAll('apex-pj-builder').forEach(initialize);
  });
})();

(() => {
  const initializeLightbox = (builder) => {
    if (builder.dataset.galleryLightboxReady === 'true') return;
    builder.dataset.galleryLightboxReady = 'true';

    let dialog;
    let image;
    let counter;
    let activeIndex = 0;
    let returnFocus;
    let pointerStartX = 0;
    let heroPointerStartX = 0;
    let heroPointerStartY = 0;
    let suppressHeroClick = false;

    const isMobile = () => window.matchMedia('(max-width: 780px)').matches;
    const hero = builder.querySelector('.lsp__gallery-main');

    const items = () => [...builder.querySelectorAll('[data-gallery-thumb]')].map((thumb) => ({
      src: thumb.dataset.src,
      srcset: thumb.dataset.srcset || '',
      alt: thumb.dataset.alt || 'Product image'
    })).filter((item) => item.src);

    const preloadNeighbors = (galleryItems) => {
      const load = () => {
        [activeIndex - 1, activeIndex + 1].forEach((index) => {
          const item = galleryItems[(index + galleryItems.length) % galleryItems.length];
          if (!item?.src) return;
          const preload = new Image();
          preload.src = item.src;
        });
      };
      if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 800 });
      else setTimeout(load, 120);
    };

    const show = (index) => {
      const galleryItems = items();
      if (!galleryItems.length) return;
      activeIndex = (index + galleryItems.length) % galleryItems.length;
      const item = galleryItems[activeIndex];
      image.classList.remove('is-loaded');
      image.removeAttribute('srcset');
      image.src = item.src;
      if (item.srcset) image.srcset = item.srcset;
      image.alt = item.alt;
      counter.textContent = `${activeIndex + 1} / ${galleryItems.length}`;
      image.onload = () => image.classList.add('is-loaded');
      if (image.complete) image.classList.add('is-loaded');
      preloadNeighbors(galleryItems);
    };

    const close = () => dialog?.close();

    const createDialog = () => {
      dialog = document.createElement('dialog');
      dialog.className = 'lsp__lightbox';
      dialog.setAttribute('aria-label', 'Product image gallery');
      dialog.innerHTML = `
        <div class="lsp__lightbox-frame">
          <button type="button" class="lsp__lightbox-close" aria-label="Close image gallery">&times;</button>
          <button type="button" class="lsp__lightbox-nav lsp__lightbox-nav--prev" aria-label="Previous image">&#8592;</button>
          <figure class="lsp__lightbox-figure">
            <img class="lsp__lightbox-image" alt="" decoding="async">
            <figcaption class="lsp__lightbox-count" aria-live="polite"></figcaption>
          </figure>
          <button type="button" class="lsp__lightbox-nav lsp__lightbox-nav--next" aria-label="Next image">&#8594;</button>
        </div>`;
      document.body.appendChild(dialog);
      image = dialog.querySelector('.lsp__lightbox-image');
      counter = dialog.querySelector('.lsp__lightbox-count');
      dialog.querySelector('.lsp__lightbox-close').addEventListener('click', close);
      dialog.querySelector('.lsp__lightbox-nav--prev').addEventListener('click', () => show(activeIndex - 1));
      dialog.querySelector('.lsp__lightbox-nav--next').addEventListener('click', () => show(activeIndex + 1));
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          show(activeIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          show(activeIndex + 1);
        }
      });
      dialog.addEventListener('pointerdown', (event) => { pointerStartX = event.clientX; });
      dialog.addEventListener('pointerup', (event) => {
        const distance = event.clientX - pointerStartX;
        if (Math.abs(distance) > 55) show(activeIndex + (distance < 0 ? 1 : -1));
      });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('lsp-lightbox-open');
        returnFocus?.focus();
      });
    };

    const openAt = (index, trigger) => {
      if (!dialog) createDialog();
      returnFocus = trigger;
      show(index);
      document.body.classList.add('lsp-lightbox-open');
      dialog.showModal();
      dialog.querySelector('.lsp__lightbox-close').focus();
    };

    /* On mobile the hero acts like a lightweight swipe carousel. The existing
       thumbnails remain the source of truth, so no duplicate images are loaded. */
    hero?.addEventListener('pointerdown', (event) => {
      if (!isMobile() || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
      heroPointerStartX = event.clientX;
      heroPointerStartY = event.clientY;
    });
    hero?.addEventListener('pointerup', (event) => {
      if (!isMobile() || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
      const deltaX = event.clientX - heroPointerStartX;
      const deltaY = event.clientY - heroPointerStartY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      const thumbs = [...builder.querySelectorAll('[data-gallery-thumb]')];
      if (!thumbs.length) return;
      const current = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      const next = (current + (deltaX < 0 ? 1 : -1) + thumbs.length) % thumbs.length;
      suppressHeroClick = true;
      builder.setGallery?.(thumbs[next]);
      thumbs[next].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      window.setTimeout(() => { suppressHeroClick = false; }, 350);
    });

    builder.querySelectorAll('.lsp__gallery-support img').forEach((supportImage, index) => {
      supportImage.addEventListener('click', (event) => {
        event.stopPropagation();
        const galleryIndex = Number(supportImage.dataset.lightboxIndex || index + 2) - 1;
        openAt(galleryIndex, supportImage);
      });
    });

    builder.addEventListener('click', (event) => {
      const clickedImage = event.target.closest('[data-gallery-image], [data-gallery-thumb] img');
      if (!clickedImage) return;
      const thumb = clickedImage.closest('[data-gallery-thumb]');
      if (isMobile() && thumb) return;
      if (suppressHeroClick && clickedImage.matches('[data-gallery-image]')) return;
      let index = 0;
      if (thumb) index = Math.max(0, Number(thumb.dataset.index || 1) - 1);
      else {
        const activeThumb = builder.querySelector('[data-gallery-thumb].is-active');
        index = Math.max(0, Number(activeThumb?.dataset.index || 1) - 1);
      }
      openAt(index, thumb || clickedImage);
    });
  };

  customElements.whenDefined('love-spell-builder').then(() => {
    document.querySelectorAll('love-spell-builder').forEach(initializeLightbox);
  });
  customElements.whenDefined('apex-pj-builder').then(() => {
    document.querySelectorAll('apex-pj-builder').forEach(initializeLightbox);
  });
})();
